/**
 * Team check-ins — the optional second history view, WP-W10.
 * `web/shapes/geo.md` §6 is the agreed shape.
 *
 * Rendered only for a session holding `workforce.view_department` or
 * `workforce.view_workspace` (`GeoScreen` decides that, the same client-side honesty
 * gate `LeaveScreen`'s Approvals section uses — never the authorisation, which the
 * server still runs on every call). `GET /employees` (Task T2-A) and `EmployeePicker`
 * (Task T2-B) exist now, but this panel has not been rewired to use them, so — like
 * `LeaveApprovalsPanel` before it — the viewer **types an employee id**.
 *
 * Naming someone else's check-ins is sensitive access: `GET /geo/checkins` refuses it
 * without a mandatory `X-Access-Reason`, so nothing is read until both an id has been
 * loaded and `AccessReasonGate` has a usable reason open — `useApiQuery`'s own
 * "there is nothing to ask yet" signal, driven from here exactly as `AlertsScreen`
 * drives it for `GET /alerts`.
 *
 * ── Trust column + "chỉ hiện nghi ngờ" filter (Pillar web, PW3) ──────────────
 *
 * `showTrust` on `CheckinsListPanel` is set here, and only here — the trust verdict
 * (migration 047) is a manager reading someone else's device signals, not something
 * "my check-ins" needs to tell an employee about their own. `suspectOnly` is state
 * `GeoScreen` owns (it feeds `geoCheckinsPath`'s `trust` parameter alongside
 * `employeeId`), so toggling it re-runs the same `useApiQuery` effect that a new
 * employee id or a newly opened reason already re-runs — no separate reload wiring
 * needed here.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { GeoCheckin } from '../../api/geo';
import type { QueryStatus } from '../../api/useApiQuery';
import type { WorkforceApiError } from '../../api/errors';
import { AccessReasonGate } from '../../access/AccessReasonGate';
import { EmptyState } from '../../states/EmptyState';
import { CheckinsListPanel } from './CheckinsListPanel';

export interface TeamCheckinsPanelProps {
  readonly employeeId: string | null;
  readonly onLoadEmployee: (id: string) => void;
  readonly reason: string;
  readonly onOpenReason: (reason: string) => void;
  readonly status: QueryStatus;
  readonly checkins: readonly GeoCheckin[] | null;
  readonly error: WorkforceApiError | null;
  readonly onReload: () => void;
  readonly locale: string;
  readonly timezone: string;
  /** "chỉ hiện nghi ngờ" — `?trust=suspect` on the next read. Never an accusation:
   * it only narrows which neutral flags are shown, the same way any other filter
   * narrows a list. */
  readonly suspectOnly: boolean;
  readonly onSuspectOnlyChange: (value: boolean) => void;
}

export function TeamCheckinsPanel(props: TeamCheckinsPanelProps): ReactNode {
  const { t } = useI18n();
  const [employeeIdInput, setEmployeeIdInput] = useState('');

  return (
    <section className="wp-section" aria-labelledby="w10-team-heading" data-testid="w10-team">
      <h2 className="wp-section__title" id="w10-team-heading">
        {t('geo.team.title')}
      </h2>
      <p className="wp-small wp-muted">{t('geo.team.intro')}</p>

      <form
        className="wp-filters"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmed = employeeIdInput.trim();
          if (trimmed === '') return;
          props.onLoadEmployee(trimmed);
        }}
      >
        <div className="wp-filters__group">
          <label className="wp-field__label" htmlFor="w10-team-employee-id">
            {t('geo.team.employeeIdLabel')}
          </label>
          <input
            className="wp-field__input"
            id="w10-team-employee-id"
            data-testid="w10-team-employee-id"
            type="text"
            inputMode="numeric"
            value={employeeIdInput}
            autoComplete="off"
            onChange={(event) => {
              setEmployeeIdInput(event.target.value);
            }}
          />
          <p className="wp-small wp-muted">{t('geo.team.employeeIdHint')}</p>
        </div>
        <div className="wp-filters__actions">
          <button
            className="wp-button wp-button--primary"
            type="submit"
            data-testid="w10-team-load"
            disabled={employeeIdInput.trim() === ''}
          >
            {t('geo.team.load')}
          </button>
        </div>
      </form>

      {props.employeeId === null ? (
        <EmptyState
          title={t('geo.team.none.title')}
          description={t('geo.team.none.body')}
          testId="w10-team-none"
        />
      ) : (
        <>
          <AccessReasonGate
            value={props.reason}
            subject={t('geo.team.subject')}
            testId="w10-team-access"
            onOpen={props.onOpenReason}
          />
          {props.reason === '' ? null : (
            <>
              <div className="wp-filters__group">
                <label className="wp-toggle" htmlFor="w10-team-trust-filter">
                  <input
                    id="w10-team-trust-filter"
                    type="checkbox"
                    data-testid="w10-team-trust-filter"
                    checked={props.suspectOnly}
                    onChange={(event) => {
                      props.onSuspectOnlyChange(event.target.checked);
                    }}
                  />
                  <span>{t('geo.team.trustFilter')}</span>
                </label>
              </div>
              <CheckinsListPanel
                status={props.status}
                checkins={props.checkins}
                error={props.error}
                onReload={props.onReload}
                subject={t('geo.team.subject')}
                emptyTitle={t('geo.team.empty.title')}
                emptyBody={t('geo.team.empty.body')}
                locale={props.locale}
                timezone={props.timezone}
                testPrefix="w10-team"
                showTrust
              />
            </>
          )}
        </>
      )}
    </section>
  );
}
